/**CORE LIBRARIES IMPORT */
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS IMPORT */
import MenuItemCard from "../Components/MenuItemCard";
import SectionHeading from "../Components/SectionHeading";

/**DATA / HOOKS / UTILS IMPORT */
import { groupMenu, ALLERGENS } from "../data/menu";
import { useMenuContent } from "../hooks/useContent";
import { sampleMenuItems } from "../data/menu.sample";
import { BOOK_TABLE_PATH } from "../data/navigation";
import usePageTitle from "../hooks/usePageTitle";
import { fadeUp, stagger, revealOnScroll } from "../utils/motion";

const MotionLink = motion.create(Link);

/** Display-only restaurant menu: no cart, checkout or online ordering. */
const Menu = () => {
  usePageTitle("Menu");
  const [searchParams] = useSearchParams();

  // Layout preview with labelled sample items: dev server only, never in production.
  const showSamples =
    process.env.NODE_ENV === "development" && searchParams.get("sample") === "1";
  // Static menu, replaced by portal-managed dishes when the backend is live.
  const { categories: menuCategories, items: liveItems } = useMenuContent();
  const items = showSamples ? sampleMenuItems : liveItems;
  const groups = groupMenu(items, menuCategories);
  const hasMenu = groups.length > 0;
  const navCategories = hasMenu ? groups : menuCategories;

  return (
    <main className="menu-page">
      <section className="page-hero" aria-labelledby="menu-title">
        <motion.div
          className="light-sweep"
          aria-hidden="true"
          animate={{ x: ["-100%", "100%"] }}
          transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
        />
        <motion.div className="page-hero-inner" initial="hidden" animate="visible" variants={stagger(0.15)}>
          <motion.p className="page-eyebrow" variants={fadeUp}>The Grey Wolf</motion.p>
          <motion.h1 id="menu-title" className="page-title" variants={fadeUp} aria-label="Our Menu">
            <span className="outline" aria-hidden="true">O</span>
            <span aria-hidden="true">ur Menu</span>
          </motion.h1>
          <motion.p className="page-subtitle" variants={fadeUp}>
            Wood-fired pizza, panuozzo, grill and more.
          </motion.p>
        </motion.div>
      </section>

      {showSamples && (
        <p className="dev-banner" role="note">
          Development preview: sample items for layout only. These are not real dishes or prices.
        </p>
      )}

      <nav className="category-nav" aria-label="Menu categories">
        <ul>
          {navCategories.map((cat) => (
            <li key={cat.slug}>
              <a href={`#${cat.slug}`}>{cat.name}</a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="menu-body">
        {hasMenu ? (
          groups.map((group) => (
            <section key={group.slug} id={group.slug} className="menu-section" aria-labelledby={`${group.slug}-heading`} tabIndex={-1}>
              <SectionHeading id={`${group.slug}-heading`} title={group.name} />
              <motion.div className="menu-grid" variants={stagger(0.08)} {...revealOnScroll(0.1)}>
                {group.items.map((item) => (
                  <motion.div key={item.id} variants={fadeUp}>
                    <MenuItemCard item={item} />
                  </motion.div>
                ))}
              </motion.div>
            </section>
          ))
        ) : (
          <motion.section className="menu-coming-soon" aria-labelledby="coming-soon-heading" variants={stagger(0.1)} {...revealOnScroll(0.2)}>
            <SectionHeading id="coming-soon-heading" title="Menu Coming Soon" />
            <motion.p className="section-text" variants={fadeUp}>
              We are putting the finishing touches to our menu. Here is what you
              can look forward to from our kitchen:
            </motion.p>
            <motion.ul className="category-tiles" variants={stagger(0.05)}>
              {menuCategories.map((cat) => (
                <motion.li key={cat.slug} id={cat.slug} variants={fadeUp} tabIndex={-1}>
                  {cat.name}
                </motion.li>
              ))}
            </motion.ul>
            <motion.div className="hero-buttons section-buttons" variants={fadeUp}>
              <MotionLink to={BOOK_TABLE_PATH} className="hero-btn" whileHover={{ y: -6, scale: 1.05 }} whileTap={{ scale: 0.96 }}>
                Book a Table
              </MotionLink>
            </motion.div>
          </motion.section>
        )}

        <details className="allergen-key">
          <summary>Allergen information</summary>
          <p className="section-text">
            Allergens are listed by number on each dish. If you have an allergy
            or intolerance, please tell a member of staff before you order.
          </p>
          <ol>
            {Object.entries(ALLERGENS).map(([code, name]) => (
              <li key={code} value={code}>{name}</li>
            ))}
          </ol>
        </details>
      </div>
    </main>
  );
};

export default Menu;
